import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { PlansTableProps } from '../types';

export function PlansTable({
  isDark,
  plans,
  isLoading,
  onEdit,
  onDelete,
  formatPrice,
  formatDuration,
}: PlansTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className={cn(
          isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'
        )}>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Код
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Название
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Длительность
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Цена
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Вариант
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Статус
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Действия
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {plans.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-12 text-slate-500">
              Тарифы не найдены
            </TableCell>
          </TableRow>
        ) : (
          plans.map((plan) => (
            <TableRow 
              key={plan.id}
              className={cn(
                'transition-colors',
                isDark 
                  ? 'border-slate-700 hover:bg-slate-800/50' 
                  : 'border-slate-100 hover:bg-slate-50'
              )}
            >
              <TableCell className={cn(
                'font-mono text-sm',
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>{plan.code}</TableCell>
              <TableCell className={cn(
                'font-medium',
                isDark ? 'text-white' : 'text-[#111111]'
              )}>{plan.title}</TableCell>
              <TableCell className={cn(
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>{formatDuration(plan.duration_value, plan.duration_unit)}</TableCell>
              <TableCell>
                <div>
                  <p className={cn(
                    'font-medium',
                    isDark ? 'text-white' : 'text-[#111111]'
                  )}>{formatPrice(plan.price)}</p>
                  {plan.old_price && (
                    <p className={cn(
                      'text-xs line-through',
                      isDark ? 'text-slate-500' : 'text-slate-400'
                    )}>{formatPrice(plan.old_price)}</p>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {plan.variant ? (
                  <Badge className="rounded-lg font-medium border-0 bg-[#C9A227]/15 text-[#9C7E1B]">
                    {plan.variant.name}
                  </Badge>
                ) : (
                  <span className={cn('text-sm', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    Базовый
                  </span>
                )}
              </TableCell>
              <TableCell>
                <Badge className={cn(
                  'rounded-lg font-medium border-0',
                  plan.is_active
                    ? 'bg-emerald-500/10 text-emerald-500'
                    : 'bg-slate-500/10 text-slate-500'
                )}>
                  {plan.is_active ? (
                    <>
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Активен
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3 h-3 mr-1" />
                      Неактивен
                    </>
                  )}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Редактировать тариф"
                    onClick={() => onEdit(plan)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark 
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-white' 
                        : 'text-slate-600 hover:bg-slate-100'
                    )}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Удалить тариф"
                    onClick={() => onDelete(plan.id)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark 
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400' 
                        : 'text-slate-600 hover:bg-slate-100 hover:text-red-600'
                    )}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
